import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconGear } from "../../icons/IconGear/IconGear";
import { KpiSelector } from "../KpiSelector/KpiSelector";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  useGetSavedKpiPreferenceQuery,
  useUpdateSavedKpiPreferenceMutation,
} from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import { setSavedKpiPreferencesData } from "../../../reducers/savedKpiPreferences";
import { getSavedKpiPreferencesForView, updateSavedKpiPreferencesForView } from "../../util/savedKpiPrefrencesUtil";
import defaultKeyMetricsContent from "../../../content/keyMetrics";
import "./KpiSelectorModal.scss";

export interface KpiSelectorProps {
  availableKpis: string[];
  defaultKpis: string[];
  saveViewName: string;
  saveViewDescription: string;
  maxSelectionsAllowed?: number;
  subHeader?: string;
  textOverrides?: { [key: string]: string };
  onOpenTrack?: () => void;
  onCancelTrack?: () => void;
  /** @param selectedKpis the checked KPIs, in the user's chosen order
   *  @param sortedAvailableKpis all availableKpis reordered to match selectedKpis (selected first, in order; unselected trailing) */
  onApplyTrack?: (selectedKpis: string[], sortedAvailableKpis: string[]) => void;
  disabled?: boolean;
}

export const KpiSelectorModal = ({
  availableKpis,
  defaultKpis,
  saveViewName,
  saveViewDescription,
  maxSelectionsAllowed = 8,
  subHeader,
  textOverrides,
  onOpenTrack = () => {},
  onCancelTrack = () => {},
  onApplyTrack = (_selectedKpis: string[], _sortedAvailableKpis: string[]) => {},
  disabled = false,
}: KpiSelectorProps) => {
  const [showModal, setShowModal] = useState(false);
  const [kpisToShow, setKpisToShow] = useState<string[]>(defaultKpis);
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  const [updateSavedKpiPreference] = useUpdateSavedKpiPreferenceMutation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["keyMetrics"]);
  const formattedSaveViewName = saveViewName;

  // The API resolves impersonation-specific saved view IDs server-side.
  useGetSavedKpiPreferenceQuery(saveViewName, { refetchOnMountOrArgChange: true });

  const savedView = savedKpiPreferencesData?.savedDataViews?.[formattedSaveViewName];

  //Primitive keys to not cause any dependency issues
  const savedViewKey = savedView?.availableKpis?.map((k) => `${k.id}:${String(k.displaydefault)}`).join(",") ?? "";
  const availableKpisKey = availableKpis.join(",");

  const toggleModal = () => {
    setShowModal((prev) => {
      if (!prev) onOpenTrack();
      return !prev;
    });
  };

  const closeModal = () => {
    onCancelTrack();
    setShowModal(false);
  };

  const selectedKpis = useMemo(() => {
    const savedKpis = savedKpiPreferencesData
      ? getSavedKpiPreferencesForView(formattedSaveViewName, savedKpiPreferencesData)
      : [];
    const filteredSavedKpis = savedKpis.filter((kpi) => availableKpis.includes(kpi));
    return filteredSavedKpis.length ? filteredSavedKpis : kpisToShow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedViewKey, availableKpisKey, kpisToShow.join(",")]);

  const updatedKpis = useMemo(
    () =>
      [...availableKpis].sort(
        (a, b) =>
          (selectedKpis.includes(a) ? selectedKpis.indexOf(a) : availableKpis.length + 1) -
          (selectedKpis.includes(b) ? selectedKpis.indexOf(b) : availableKpis.length + 1),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKpis.join(""), availableKpisKey],
  );

  const submitSelectedKpis = async (selectedKpis: string[]) => {
    setKpisToShow(selectedKpis);
    const sortedKpisBySelection = [...availableKpis].sort(
      (a, b) =>
        (selectedKpis.includes(a) ? selectedKpis.indexOf(a) : availableKpis.length + 1) -
        (selectedKpis.includes(b) ? selectedKpis.indexOf(b) : availableKpis.length + 1),
    );

    onApplyTrack(selectedKpis, sortedKpisBySelection);
    setShowModal(false);

    const { preferenceDetails, value } = updateSavedKpiPreferencesForView(
      formattedSaveViewName,
      saveViewDescription,
      sortedKpisBySelection,
      selectedKpis,
      savedKpiPreferencesData || { savedDataViews: {} },
      defaultKeyMetricsContent,
      t,
    );

    dispatch(setSavedKpiPreferencesData(preferenceDetails));

    try {
      await updateSavedKpiPreference({ viewId: saveViewName, value }).unwrap();
    } catch {
      // Keep the local preference state so the selected KPIs remain visible if the update fails.
    }
  };

  return (
    <>
      <div
        onClick={toggleModal}
        onKeyDown={(e: { key: string; preventDefault: () => void }) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleModal();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        className={`${showModal ? "kpiFilterModal__backdrop" : ""}`}
      ></div>
      <button
        className={`kpiFilterModal__button ${showModal && "kpiFilterModal__button--button-clicked"} ${disabled && "kpiFilterModal__button--hover-disabled"}`}
        onClick={toggleModal}
        disabled={disabled}
        data-testid="kpi-filter-button"
        aria-label="KPI Filter"
      >
        <IconGear noBorder={true} />
      </button>
      <div className="kpiFilterModal__modal">
        <KpiSelector
          show={showModal}
          availableKpis={updatedKpis}
          selectedKpis={selectedKpis}
          onCloseCallbackFn={closeModal}
          minSelectionsAllowed={1}
          maxSelectionsAllowed={maxSelectionsAllowed}
          onSubmitFn={submitSelectedKpis}
          description={subHeader}
          textOverride={textOverrides}
        />
      </div>
    </>
  );
};
